import {get, isNil, isEmpty} from 'lodash'
import {getPartType, PartType} from 'helpers/parts'
import {defaultIfBlank} from 'helpers/string'
import {ApplyCostType, getPartSuppliers} from 'helpers/partSuppliers'
import {PACK_TYPE_LABOUR_LABELS} from 'helpers/constants'

export const nullPack = {
  baseCost: 0,
  baseCostIsMin: false,
  basePrice: 0,
  basePriceIsMin: false,
  baseType: false,
  createdDate: null,
  cumulative: false,
  id: null,
  modifiedDate: null,
  msrp: 0,
  multiple: 0,
  packTypeId: null,
  partId: null,
  sizeFactored: false,
  standardCost: 0,
  prices: [],
  guid: null,
}

export const createNewPack = (partTemplateId, packTypeId, isPartTypeLabour = false) => ({
  partTemplateId,
  description: packTypeId,
  packTypeId: -1,
  id: -1,
  labour: isPartTypeLabour,
  guid: null,
})

export const defaultNewPackType = () => ({
  partTemplateId: null,
  description: null,
  packTypeId: null,
  id: null,
  labour: null,
  guid: null,
})


export const PricingMethod = {
  NON_CUMULATIVE: {
    id: 0,
    key: 0,
    label: 'Non Cumulative',
  },
  CUMULATIVE: {
    id: 1,
    key: 1,
    label: 'Cumulative',
  },
}

export const isCumulativePricingMethod = (pricingMethod) => {
  return pricingMethod === PricingMethod.CUMULATIVE.id
}

export const isNonCumulativePricingMethod = (pricingMethod) => {
  return pricingMethod === PricingMethod.NON_CUMULATIVE.id
}

export const mapPricingMethodToOptions = () => {
  return Object.values(PricingMethod)
}

export const getPricingMethod = (cumulative) => {
  return cumulative
      ? PricingMethod.CUMULATIVE.id
      : PricingMethod.NON_CUMULATIVE.id
}

export const getCumulativeValue = (value) => {
  return value === 1
}

export const CalculationMethod = {
  QUANTITY: {
    id: 0,
    key: 0,
    label: 'Quantity Sold',
  },
  SIZE: {
    id: 1,
    key: 1,
    label: 'Size Sold',
  },
}

export const mapCalculationMethodToOptions = () => {
  return Object.values(CalculationMethod)
}

export const getCalculationMethod = (sizeFactored) => {
  return sizeFactored
      ? CalculationMethod.SIZE.id
      : CalculationMethod.QUANTITY.id
}

export const getSizeFactoredValue = (value) => {
  return value === 1
}

export const getPartPacks = (part, isComplexPricingEnabled = false) => {
  const partPacks = get(part, 'packs', [])
  if (!isComplexPricingEnabled) {
    const basePartPack = getBasePack(partPacks)
    return [basePartPack]
  }

  return partPacks
}

export const getBasePack = (packs = []) => {
  return isNil(packs) || packs.length === 0
      ? nullPack
      : packs.find(pack => pack.baseType === true) || packs[0]
}

export const getDefaultPack = (packs = []) => {
  return isEmpty(packs)
      ? null
      : packs.find(pack => pack.default === true)
}

export const getSelectedPartPackId = (part) => {
  const partPacks = get(part, 'packs', [])
  return get(getBasePack(partPacks), 'id', null)
}

export const nullPackType = {
  createdDate: null,
  description: '',
  id: null,
  labour: false,
  modifiedDate: null,
}

export const getBasePackType = (packTypeId, packTypes = []) => {
  return packTypes.length === 0 || isNil(packTypeId)
      ? nullPackType
      : packTypes.find(packType => packType.id === packTypeId)
}

export const mapPackTypesToOptions = (packTypes = []) => {
  return packTypes.map(packType => {
    return {
      id: packType.id,
      key: packType.id,
      label: packType.description,
    }
  })
}

export const getPartPackFor = (partPackId, partPacks) => {
  return isNil(partPacks) ? null : partPacks.find(partPack => partPack.id === partPackId)
}

export const getPartPackWithValue = (key, value, partPacks) => {
  return isNil(partPacks) ? null : partPacks.find(partPack => partPack[key] === value)
}

export const partPackHasSuppliers = (partPack) => {
  return !isNil(partPack) && !isEmpty(partPack.suppliers)
}

export const getPartPackSupplierOptions = (partPack) => {
  const suppliers = get(partPack, 'suppliers', [])
  return [
    {
      key: null,
      value: null,
      label: 'No Supplier',
    },
    ...suppliers.map(getPartPackSupplierOption),
  ]
}

export const getPartPackSupplierOption = (partSupplier) => {
  return {
    key: get(partSupplier, 'companyId'),
    value: get(partSupplier, 'companyId'),
    label: get(partSupplier, 'company.name'),
  }
}

export const getDefaultSupplierIdForPack = (user, partPack) => {
  return partPack && get(user, 'settings.quotingUseDefaultSupplier', false)
      ? findDefaultSupplierId(partPack)
      : null
}

export const findDefaultSupplierId = (partPack) => {
  return get(getPartSuppliers(partPack).find(supplier => supplier.default), 'companyId')
}

export const updatePartPack = (partPack, key, value) => {
  const updatedPartPack = {...partPack}
  switch (key) {
    case 'cumulative':
      updatedPartPack[key] = getCumulativeValue(value)
      break
    case 'sizeFactored':
      updatedPartPack[key] = getSizeFactoredValue(value)
      break
    default:
      updatedPartPack[key] = value
      break
  }

  return updatedPartPack
}

export const updatePartPacksFor = (partPackId, partPacks, updatedPartPack) => {
  const updatedPartPacks = [...partPacks]

  return updatedPartPacks.map(partPack => {
    return (partPack.id === partPackId)
        ? updatedPartPack
        : partPack
  })
}

export const updatePartPackFor = (part, partPackId, key, value) => {
  const partPacks = get(part, 'packs', [])
  if (isEmpty(partPacks))
    return part

  const partPackToUpdate = getPartPackFor(partPackId, partPacks)
  const updatedPartPack = updatePartPack(partPackToUpdate, key, value)
  const updatedPartPacks = updatePartPacksFor(partPackId, partPacks, updatedPartPack)

  return {
    ...part,
    packs: updatedPartPacks,
  }
}

export const ApplyPricingType = {
  APPLY_BASE_PRICE: {
    id: 0,
    label: 'Apply a Base Price',
  },
  APPLY_MINIMUM_PRICE: {
    id: 1,
    label: 'Apply a Minimum Price',
  },
}

export const ApplyPricingTypeForNewPriceListUI = {
  APPLY_BASE_PRICE: {
    id: 0,
    label: 'Base Price of',
  },
  APPLY_MINIMUM_PRICE: {
    id: 1,
    label: 'Minimum Price of',
  },
}

export const getApplyPricingMethodValueForNewPriceListUI = (applyPricingType) => {
  return applyPricingType
      ? ApplyPricingTypeForNewPriceListUI.APPLY_MINIMUM_PRICE.id
      : ApplyPricingTypeForNewPriceListUI.APPLY_BASE_PRICE.id
}

export const getApplyCostTypeValueForNewPriceListUI = (baseCostIsMin) => {
  return baseCostIsMin
      ? ApplyCostType.APPLY_MINIMUM_COST.id
      : ApplyCostType.APPLY_BASE_COST.id
}
export const updateBasePricingFor = (partPackId, key, value, part) => {
  let partPacks = get(part, 'packs', [])
  partPacks = partPacks.map(partPack => {
    if (partPack.id === partPackId) {
      partPack['basePriceIsMin'] = value === ApplyPricingType.APPLY_MINIMUM_PRICE.id
    }
    return partPack
  })

  part['packs'] = partPacks

  return part
}

export const addPartPackToPart = (part, partPack) => {
  const partPacks = get(part, 'packs', [])
  partPacks.push(partPack)
  part['packs'] = partPacks

  return part
}

export const deletePartPackFromPartFor = (deletedPartPackId, part) => {
  const partPacks = get(part, 'packs', [])
  const updatedPartPacks = partPacks.filter(partPack => partPack.id !== deletedPartPackId)
  const deletedPartPack = partPacks.find(partPack => partPack.id === deletedPartPackId)

  const isDeletedDefault = get(deletedPartPack, 'default', false)
  if (isDeletedDefault) {
    const baseTypePartPack = partPacks.find(partPack => partPack.baseType)
    if (!isNil(baseTypePartPack)) {
      baseTypePartPack['default'] = true
    }
  }

  return {
    ...part,
    packs: updatedPartPacks,
    deletedPacks: deletedPartPack ? [deletedPartPack] : [],
  }
}

export const updatePartPacksForDefault = (part, partPackId) => {
  const partPacks = get(part, 'packs', [])
  part['packs'] = partPacks.map(partPack => {
    partPack['default'] = (partPack.id === partPackId)
    return partPack
  })
  return part
}

export const isMultipleFieldDisabled = (baseType) => {
  return baseType === true
}

export const isMultipleFieldReadOnly = (partPackId, packTypes) => {
  const packTypeHours = getPartPackWithValue('description', PACK_TYPE_LABOUR_LABELS.HOURS, packTypes)
  const packTypeDays = getPartPackWithValue('description', PACK_TYPE_LABOUR_LABELS.DAYS, packTypes)

  if (isNil(partPackId))
    return false

  return partPackId === packTypeHours.id || partPackId === packTypeDays.id
}

export const filterPackTypesByLabour = (partTemplateId, packTypes = [], partTemplates = [], newPackType = null) => {
  const filteredPartTemplate = partTemplates.find(partTemplate => partTemplate.id === partTemplateId)
  const isPartTypeLabour = filteredPartTemplate && getPartType(filteredPartTemplate.partType).labour
  const filtered = packTypes.filter(packType => packType.labour === isPartTypeLabour)
  if (newPackType && newPackType.description) filtered.push({...newPackType, isPartTypeLabour})

  return filtered
}

export const filterExistingPackTypes = (packTypes, partPacks) => {
  const packTypeIds = partPacks.map(partPack => partPack.packTypeId)
  return packTypes.filter(packType => !packTypeIds.includes(packType.id))
}

export const checkIfPackTypeIsNew = (packTypeId, partTemplateId) => {
  return (packTypeId && typeof packTypeId === 'string' && defaultIfBlank(packTypeId))
      ? createNewPack(partTemplateId, packTypeId)
      : defaultNewPackType()
}

export const getMinPackTypeId = (partTemplateId, packTypes = [], partTemplates = []) => {
  const packTypesByLabour = filterPackTypesByLabour(partTemplateId, packTypes, partTemplates)
  return Math.min(...packTypesByLabour.map(packType => packType.id))
}

export const getPackTypeValue = (packTypeId, newPackType) => {
  return !isNil(newPackType)
      ? newPackType.packTypeId
      : packTypeId
}

export const isNewPartPackAdded = (currentPart, updatedPart) => {
  const currentPartPacks = get(currentPart, 'packs', [])
      .filter(partPack => partPack.id !== -1)
  const updatedPartPacks = get(updatedPart, 'packs', [])
  return currentPartPacks.length < updatedPartPacks.length
}

export const getNewlyAddedPartPackId = (currentPart, updatedPart) => {
  const currentPartPacks = get(currentPart, 'packs', []).filter(partPack => partPack.id !== -1)
  const updatedPartPacks = get(updatedPart, 'packs', [])
  const currentPartPackIds = new Set(currentPartPacks.map(partPack => partPack?.id))
  const newPartPack = updatedPartPacks.find(partPack => !currentPartPackIds.has(partPack.id))
  return get(newPartPack, 'id', null)
}

export const isAssemblyPartWithNoPrice = (part, price) => {
  const partIsAssembly = get(part, 'type') === PartType.ASSEMBLY.id

  return (partIsAssembly && !price)
}
